import { ref } from 'vue'
import { TransformLearningProgressService } from '../services/TransformLearningProgressService'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { useQuizRestart } from './useQuizRestart'
import { useTransformOwnerContext } from './useTransformOwnerContext'
import { handleErrors } from '../utils/errorHandler'

// Escalate auto-save failures to telemetry only after this many in a row.
const DRAFT_FAILURE_REPORT_THRESHOLD = 3

/**
 * Manages quiz state and persistence for a single quiz lesson.
 *
 * Seeds from the lesson's existing bcst_lesson_progress doc so users can resume
 * exactly where they left off. Draft answers are saved automatically after each
 * selection; the final submission calculates the score and marks the lesson complete.
 *
 * Must be called from a component setup (the underlying service uses inject).
 *
 * @param {Object} opts
 * @param {string} opts.lessonId
 * @param {string} opts.courseId
 * @param {number}  [opts.initialCurrentQuestionIndex]
 * @param {Object}  [opts.initialDraftAnswers] - { [questionId]: selectedOptionId }
 * @param {boolean} [opts.initialCompleted]
 * @param {number|null} [opts.initialScorePercent]
 */
export function useQuizProgress({
  lessonId,
  courseId,
  initialCurrentQuestionIndex = 0,
  initialDraftAnswers = {},
  initialCompleted = false,
  initialScorePercent = null,
}) {
  const progressService = TransformLearningProgressService()
  const store = useTransformLearningStore()
  const { restartQuiz: resetPersistedQuiz } = useQuizRestart()

  const currentQuestionIndex = ref(Math.max(0, Number(initialCurrentQuestionIndex) || 0))
  const answers = ref({ ...initialDraftAnswers })
  const isCompleted = ref(initialCompleted === true)
  const scorePercent = ref(initialScorePercent !== undefined ? initialScorePercent : null)
  const lastSavedAt = ref(null)
  // Foreground processing state (submit / restart). Drives the result-panel Retake spinner.
  const isSaving = ref(false)
  // Background auto-save state — drives the "Saving..." nav button while an answer persists.
  const isAutoSaving = ref(false)
  const saveError = ref(null)

  // Questions whose answer is CONFIRMED PERSISTED on the server — this, not the local
  // selection, drives the "X of Y Questions Answered" counter. Seeded from the resumed
  // draft so a returning user sees the right count immediately.
  const isAnswered = (v) => (Array.isArray(v) ? v.length > 0 : v != null)
  const savedQuestionIds = ref(
    new Set(Object.keys(initialDraftAnswers).filter(k => isAnswered(initialDraftAnswers[k])))
  )

  let consecutiveDraftFailures = 0
  // The in-flight auto-save, if any — submit awaits it so a draft (completed:false) can
  // never land after the submit's completed:true write.
  let draftPromise = null
  // Coalescing + dedupe: only one save runs at a time; a change during a save queues one
  // more, and an identical payload is never re-sent.
  let saveQueued = false
  let lastPersistedJson = JSON.stringify([
    Math.max(0, Number(initialCurrentQuestionIndex) || 0),
    { ...initialDraftAnswers },
  ])

  function answersEqual(a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
      const arrA = Array.isArray(a) ? a : []
      const arrB = Array.isArray(b) ? b : []
      return arrA.length === arrB.length && arrA.every((v, i) => v === arrB[i])
    }
    return a === b
  }

  // Record the user's answer for one question and persist it immediately (auto-save).
  // Never navigates — the user stays on the current question until they click Next.
  function selectAnswer(questionId, optionId) {
    if (isCompleted.value || !questionId) return
    // Skip a truly redundant write, EXCEPT when the last save failed — re-selecting the
    // same option is the documented retry path, so let it through to re-attempt.
    if (answersEqual(answers.value[questionId], optionId) && !saveError.value) return
    answers.value = { ...answers.value, [questionId]: optionId }
    scheduleSave({ showSaving: true })
  }

  // Navigate to a question by index and persist the position in the background (no
  // visible saving state — that's reserved for answer changes).
  function goToQuestion(index) {
    currentQuestionIndex.value = index
    scheduleSave({ showSaving: false })
  }

  // Persist answers + position. Coalesces concurrent calls (one in-flight, one queued),
  // skips identical payloads, and only shows the saving state for answer-driven saves.
  // On failure the local answer is kept and an error is surfaced so the user can retry.
  function scheduleSave({ showSaving = false } = {}) {
    // Admin "view-as" is read-only: the server derives the user from the TOKEN, so this
    // draft save would write onto the ADMIN's own progress, not the viewed user's.
    if (useTransformOwnerContext().isActive.value) return
    if (isCompleted.value || isSaving.value) return
    if (showSaving) isAutoSaving.value = true
    if (draftPromise) {
      saveQueued = true
      return
    }
    draftPromise = (async () => {
      try {
        do {
          saveQueued = false
          const idx = currentQuestionIndex.value
          const snapshot = { ...answers.value }
          const json = JSON.stringify([idx, snapshot])
          if (json === lastPersistedJson) break // dedupe: nothing changed
          try {
            await progressService.saveLessonProgress({
              lessonId,
              courseId,
              currentQuestionIndex: idx,
              draftAnswers: snapshot,
              completed: false,
            })
            lastPersistedJson = json
            lastSavedAt.value = new Date()
            saveError.value = null
            consecutiveDraftFailures = 0
            // Counter reflects only what actually persisted.
            savedQuestionIds.value = new Set(Object.keys(snapshot).filter(k => isAnswered(snapshot[k])))
          } catch (_err) {
            if (import.meta.env.DEV) console.error('[UQP] auto-save failed', _err)
            consecutiveDraftFailures += 1
            saveError.value = 'We couldn\'t save your answer. Please try again.'
            if (consecutiveDraftFailures >= DRAFT_FAILURE_REPORT_THRESHOLD) {
              handleErrors(`useQuizProgress.saveDraft-${lessonId}`, _err, { logToConsole: false })
            }
            break // keep the local selection; the next change (retry) will re-attempt
          }
        } while (saveQueued)
      } finally {
        draftPromise = null
        isAutoSaving.value = false
      }
    })()
  }

  /**
   * Reset the persisted quiz progress so the user can retake it.
   * Clears local answers/score and mirrors the reset into the store.
   * @returns {Promise<boolean>}
   */
  async function restartQuiz() {
    if (isSaving.value) return false
    isSaving.value = true
    try {
      // Only clear local + store state once the server confirms the reset. If the gateway
      // reports failure, keep the completed result on screen so the UI can't diverge from
      // the still-persisted attempt. Delegates the gateway call + store mirror to
      // useQuizRestart so the store patch (which must also clear `passed`/`correctCount`/
      // `totalCount`, not just score) stays single-sourced across every restart entry point.
      const ok = await resetPersistedQuiz({ lessonId, courseId })
      if (!ok) {
        saveError.value = 'Couldn\'t restart the quiz. Please try again.'
        return false
      }
      isCompleted.value = false
      scorePercent.value = null
      answers.value = {}
      currentQuestionIndex.value = 0
      savedQuestionIds.value = new Set()
      lastPersistedJson = JSON.stringify([0, {}])
      lastSavedAt.value = new Date()
      saveError.value = null
      return true
    } catch (_err) {
      if (import.meta.env.DEV) console.error('[UQP] restart failed', _err)
      return false
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Submit the quiz to the backend for server-side scoring and persistence.
   * The backend resolves the correct option ids, calculates the score, writes the
   * result to bcst_lesson_progress, and returns the final score + pass flag.
   *
   * @param {Array<{id:string}>} questions
   */
  async function submitQuiz(questions) {
    // Read-only in the admin view-as context (would score/persist onto the admin's record).
    if (useTransformOwnerContext().isActive.value) return
    if (isCompleted.value || isSaving.value) return
    // Take the single foreground loading state up front so one click drives one
    // uninterrupted submit (no second click).
    isSaving.value = true
    saveError.value = null

    // Wait out an in-flight auto-save so its completed:false can't overwrite our result.
    if (draftPromise) {
      try { await draftPromise } catch (_e) { /* draft errors are non-fatal here */ }
    }

    const answersPayload = {}
    for (const q of questions) {
      if (answers.value[q.id] != null) {
        answersPayload[q.id] = answers.value[q.id]
      }
    }

    try {
      const result = await progressService.submitQuiz({
        lessonId,
        courseId,
        answers: answersPayload,
      })

      const score = result?.scorePercent ?? null
      const passed = result?.passed ?? false
      const correct = result?.correct ?? 0
      const total = result?.total ?? null

      isCompleted.value = true
      scorePercent.value = score
      lastSavedAt.value = new Date()

      // Mirror the FULL server result into the store so the status badge, sidebar,
      // continue card, and category grid update immediately without a reload. `passed`
      // (and correctCount/totalCount) must be included: buildCategoryDetail and
      // isLessonTrulyCompleted prefer the persisted `passed` flag, so omitting it would
      // leave a prior attempt's stale value in place (e.g. a passing retake of a
      // previously-failed quiz would still show "Not Passed" until a full reload).
      store.applyLessonProgress(lessonId, {
        completed: true,
        scorePercent: score,
        passed,
        correctCount: correct,
        totalCount: total,
        ...(result?.passThresholdPercent != null && { passThresholdPercent: result.passThresholdPercent }),
        percentComplete: 100,
        draftAnswers: {},
      })

      return { score, passed, correct }
    } catch (_err) {
      if (import.meta.env.DEV) console.error('[UQP] submit failed', _err)
      const backendError = _err?.response?.data?.error || _err?.response?.data?.message
      saveError.value = backendError
        ? `Submission failed: ${backendError}.`
        : 'Submission failed. Please try again.'
    } finally {
      isSaving.value = false
    }
  }

  return {
    currentQuestionIndex,
    answers,
    isCompleted,
    scorePercent,
    lastSavedAt,
    isSaving,
    isAutoSaving,
    savedQuestionIds,
    saveError,
    selectAnswer,
    goToQuestion,
    restartQuiz,
    submitQuiz,
  }
}
